import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { path } from "./therapyPaths";
import TherapyNav from "./TherapyNav";
import TherapyFooter from "./TherapyFooter";
import { Icon } from "./Icons";
import "./Therapy.css";
import "./TherapyPages.css";

/**
 * FAQ rewritten for group practice voice.
 *
 * Voice rules used:
 *   - "Harbor Therapy" or "we" / "our clinicians" — never "I"
 *   - Acknowledge variation between clinicians where it exists
 *     (cadence, fees, modalities) rather than implying one approach
 *   - Group-specific questions added: switching therapists, fee variation
 */
const FAQ_GROUPS = [
    {
        category: "Getting started",
        items: [
            {
                q: "How do I know if therapy is right for me?",
                a: "You don't have to be in crisis to start. Most of our clients come in because something feels off, stuck, or harder than it should be — not because they've hit rock bottom. The free consultation is the easiest way to figure out if it makes sense to work with us. If it doesn't, we'll tell you, and we'll usually be able to point you somewhere that might.",
            },
            {
                q: "How do I choose between your therapists?",
                a: "Start by browsing the therapist directory and using the filters — specialization, gender, whether they see couples. Read a few profiles that catch your eye. If you're still not sure, schedule a free consultation; our intake coordinator will hear what you're looking for and recommend a clinician (or two) we think would fit. You're never locked in by browsing — you can take your time.",
            },
            {
                q: "What happens in the first session?",
                a: "Your therapist will spend time on what's bringing you in, your history, and what you're hoping to get out of the work. They'll ask questions, share what they're noticing, and toward the end give you their initial sense of what to focus on. There's no homework or pressure — the first session is for both of you to start understanding the work.",
            },
            {
                q: "How often will we meet?",
                a: "Cadence varies by clinician and by client. Most clients start weekly. After the first month or two, you and your therapist will talk about what makes sense — some stay weekly, some shift to biweekly, and some come in more intensively when something acute is happening. Couples typically stay weekly for at least the first three months.",
            },
            {
                q: "How long will I be in therapy?",
                a: "Honestly, it depends. Some clients come in with a specific issue and wrap up in a few months. Others stay for years and the work evolves. Our clinicians talk explicitly about goals and progress along the way, and won't keep you in therapy longer than is useful.",
            },
        ],
    },
    {
        category: "About our clinicians",
        items: [
            {
                q: "Are all of your therapists licensed?",
                a: "Yes. Every clinician at Harbor Therapy is independently licensed in New York. Several are also licensed in New Jersey, which means they can see telehealth clients in both states. Credentials are listed on each therapist's profile, along with their years in practice and specialized training.",
            },
            {
                q: "Do all your therapists charge the same fee?",
                a: "No. Fees range from $140 to $200 per individual session and $185 to $225 for couples, depending on the clinician's experience, training, and specialty. Each therapist's fee is shown clearly on their profile and at booking. Sliding-scale options are available across the practice — see the fees question below.",
            },
            {
                q: "Can I switch therapists if it's not the right fit?",
                a: "Yes, and we want you to. The match between you and your therapist matters more than almost anything else, and a poor fit isn't anyone's fault. If you want to switch, talk to your current therapist or email hello@harbortherapy.com and we'll help you transition to someone else on the team. We do this often enough that there's no awkwardness about it.",
            },
            {
                q: "Do your therapists consult with each other on cases?",
                a: "We meet weekly as a group for case consultation, with appropriate de-identification of client information. This is standard practice and helps each clinician get input from colleagues with different specialties. Your specific identity and details are not shared, and confidentiality is preserved.",
            },
            {
                q: "What modalities do your clinicians use?",
                a: "Across the practice, we use CBT, EMDR, IFS, Gottman Method, EFT, ACT, DBT, psychodynamic, somatic experiencing, mindfulness-based, and narrative therapy. Each clinician's primary modalities are listed on their profile. Most of us draw from multiple approaches depending on what fits the client.",
            },
        ],
    },
    {
        category: "Insurance & payment",
        items: [
            {
                q: "Do you take insurance?",
                a: "Harbor Therapy is out-of-network, which means we don't bill insurance directly. We provide a monthly superbill you can submit to your insurance for reimbursement. Many PPO plans reimburse 50–80% of session fees after a deductible. We recommend calling your insurance and asking about your out-of-network mental health benefits before booking.",
            },
            {
                q: "Why don't you take insurance?",
                a: "Insurance panels require a diagnosis to be on your permanent medical record, limit what kind of work clinicians can do (typically only short-term, symptom-focused approaches), and often dictate how many sessions you can have. Working out-of-network gives us more flexibility and protects your privacy.",
            },
            {
                q: "Do you offer a sliding scale?",
                a: "Yes. Most of our clinicians hold a small number of reduced-fee slots for clients experiencing financial hardship. Availability varies by therapist and is often filled, but we maintain waitlists. Ask during your consultation if cost is a barrier — we'd rather know than have you not reach out.",
            },
            {
                q: "What forms of payment do you accept?",
                a: "All major credit cards, HSA/FSA cards, and ACH bank transfers through our secure client portal. Payment is processed automatically at the time of session.",
            },
        ],
    },
    {
        category: "Logistics",
        items: [
            {
                q: "Where is your office?",
                a: "142 Montague Street, Suite 4B, in Brooklyn Heights. The office is two blocks from the Court Street / Borough Hall station and accessible by the 2, 3, 4, 5, R, and N trains. The building has an elevator.",
            },
            {
                q: "Do you offer telehealth?",
                a: "Yes. Our clinicians are licensed in New York, and several are also licensed in New Jersey, so we can see clients via secure video anywhere in those two states. Most clients mix in-person and telehealth depending on the week. Telehealth licensure for each therapist is listed on their profile.",
            },
            {
                q: "What's your cancellation policy?",
                a: "We require 24 hours notice to cancel or reschedule. Late cancellations and no-shows are charged the full session fee. The reason is that your therapist holds your time slot — when you cancel last minute, that hour can't be filled. Full details are on the policies page.",
            },
            {
                q: "Can I text or email my therapist between sessions?",
                a: "For scheduling and brief logistics, yes — through our secure client portal. For clinical content, we save it for sessions. If something urgent comes up between sessions, the portal has a message function each clinician checks daily on weekdays.",
            },
        ],
    },
    {
        category: "Privacy & confidentiality",
        items: [
            {
                q: "Is what I tell my therapist confidential?",
                a: "Yes, with the legally required exceptions all therapists operate under: imminent risk of harm to yourself or someone else, suspected abuse of a child or vulnerable adult, and certain court orders. Your therapist will go over this in detail at intake.",
            },
            {
                q: "Will my information be shared between clinicians at Harbor Therapy?",
                a: "Only in the context of clinical consultation, with appropriate de-identification — meaning your therapist may discuss the work in our weekly case consultation without naming you or sharing identifying details. Your records are not shared between clinicians, and only your therapist has direct access to your file.",
            },
            {
                q: "How are my records stored?",
                a: "We use a HIPAA-compliant electronic health record system. Records are encrypted at rest and in transit, and only your specific therapist has access to your full file. Records are kept for the period required by New York and New Jersey state law.",
            },
        ],
    },
];

function FAQItem({ item, isOpen, onToggle }) {
    return (
        <div className={`faq-item ${isOpen ? "faq-item-open" : ""}`}>
            <button className="faq-question" onClick={onToggle} aria-expanded={isOpen}>
                <span>{item.q}</span>
                <span className="faq-icon">
                    <Icon name="chevron" size={18} />
                </span>
            </button>
            {isOpen && (
                <div className="faq-answer">
                    <p>{item.a}</p>
                </div>
            )}
        </div>
    );
}

function TherapyFAQ() {
    const [openItems, setOpenItems] = useState(new Set());

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const toggle = (id) => {
        setOpenItems((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    return (
        <main className="demo demo-therapy">
            <TherapyNav />

            <section className="page-header">
                <div className="demo-container">
                    <p className="demo-eyebrow">Frequently asked</p>
                    <h1>The questions that come up most.</h1>
                    <p className="page-lede">
                        If you don't see what you're looking for, the free consultation
                        is the right place to ask. Or email{" "}
                        <a href="mailto:hello@harbortherapy.com">hello@harbortherapy.com</a>{" "}
                        and we'll get back to you.
                    </p>
                </div>
            </section>

            <section className="demo-section">
                <div className="demo-container faq-container">
                    {FAQ_GROUPS.map((group, gi) => (
                        <div key={gi} className="faq-group">
                            <h2 className="faq-category">{group.category}</h2>
                            <div className="faq-items">
                                {group.items.map((item, ii) => {
                                    const id = `${gi}-${ii}`;
                                    return (
                                        <FAQItem
                                            key={id}
                                            item={item}
                                            isOpen={openItems.has(id)}
                                            onToggle={() => toggle(id)}
                                        />
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            <section className="demo-section demo-cta">
                <div className="demo-container">
                    <h2>Still have questions?</h2>
                    <p>
                        The fastest way to get answers tailored to your situation is a
                        free 20-minute consultation. No pressure to book anything
                        afterward.
                    </p>
                    <div className="demo-actions demo-actions-center">
                        <Link to={path("booking")} className="btn-primary">
                            Schedule a consultation
                        </Link>
                        <Link to={path("therapists")} className="btn-secondary">
                            Browse therapists
                        </Link>
                    </div>
                </div>
            </section>

            <TherapyFooter />
        </main>
    );
}

export default TherapyFAQ;